import React, { useState, useEffect, useMemo } from 'react';

// A recursive helper to flatten a sub-tree for the second dropdown
const flattenSubTree = (subTree, pathPrefix) => {
    let options = [];
    Object.keys(subTree).forEach(subCategoryName => {
        const currentPath = `${pathPrefix} > ${subCategoryName}`;
        options.push(currentPath);
        if (Array.isArray(subTree[subCategoryName])) {
            subTree[subCategoryName].forEach(leafName => {
                options.push(`${currentPath} > ${leafName}`);
            });
        }
    });
    return options;
};

export default function CategorySelector({ value, onChange, categoryTree }) {
    const [selectedTopLevel, setSelectedTopLevel] = useState('');
    const [selectedSubPath, setSelectedSubPath] = useState('');

    // When the component first loads or the 'value' prop changes,
    // parse the full path to set the state of both dropdowns.
    useEffect(() => {
        if (value) {
            const parts = value.split(' > ');
            setSelectedTopLevel(parts[0]);
            setSelectedSubPath(value);
        } else {
            setSelectedTopLevel('');
            setSelectedSubPath('');
        }
    }, [value]);

    // When the user changes the top-level category...
    const handleTopLevelChange = (e) => {
        const newTopLevel = e.target.value;
        setSelectedTopLevel(newTopLevel);
        // Reset the sub-category selection, as the options will change
        setSelectedSubPath('');
        // Inform the parent that the value has been cleared for now
        onChange(''); 
    };

    // When the user makes a final selection from the sub-category dropdown...
    const handleSubPathChange = (e) => {
        const newSubPath = e.target.value;
        setSelectedSubPath(newSubPath);
        // Inform the parent of the final, complete path
        onChange(newSubPath);
    };

    // This generates the options for the second dropdown.
    // useMemo prevents it from being recalculated on every single render.
    const subCategoryOptions = useMemo(() => {
        if (!selectedTopLevel || !categoryTree[selectedTopLevel]) {
            return [];
        }
        return flattenSubTree(categoryTree[selectedTopLevel], selectedTopLevel);
    }, [selectedTopLevel, categoryTree]);

    return (
        <div className="flex flex-col md:flex-row gap-2">
            {/* Dropdown 1: Top-Level Categories */}
            <select
                value={selectedTopLevel}
                onChange={handleTopLevelChange}
                className="w-full md:w-1/3 p-1 border border-gray-300 rounded-md"
            >
                <option value="">-- Select Category --</option>
                {Object.keys(categoryTree).map(topLevel => (
                    <option key={topLevel} value={topLevel}>{topLevel}</option>
                ))}
            </select>

            {/* Dropdown 2: Sub-Categories (Levels 2 & 3) */}
            <select
                value={selectedSubPath}
                onChange={handleSubPathChange}
                disabled={!selectedTopLevel} // Disabled until a top-level is chosen
                className="w-full md:w-2/3 p-1 border border-gray-300 rounded-md disabled:bg-gray-100"
            >
                <option value="">-- Select Sub-Category --</option>
                {subCategoryOptions.map(path => (
                    <option key={path} value={path}>
                        {/* Show only the sub-path for clarity */}
                        {path.substring(selectedTopLevel.length + 3)}
                    </option>
                ))}
            </select>
        </div>
    );
}